import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { selectBothFlowsUnlocked, useFlowStore } from '../store';

const SWITCH_LABEL = {
    v1: 'Using V1 (custodial wallet). Switch to V2 (browser wallet)',
    v2: 'Using V2 (browser wallet). Switch to V1 (custodial wallet)',
} as const;

interface FlowToggleProps {
    compact?: boolean;
    className?: string;
}

/**
 * The V1/V2 button in the header. Shown only when both flows are unlocked for
 * the account; its label is the flow in use, and a click switches to the
 * other one (a full reload, see the store's `switchTo`).
 */
const FlowToggle: React.FC<FlowToggleProps> = ({
    compact = false,
    className,
}) => {
    const active = useFlowStore((state) => state.active);
    const visible = useFlowStore(selectBothFlowsUnlocked);
    const switchTo = useFlowStore((state) => state.switchTo);

    if (!visible || active === null) return null;

    const label = SWITCH_LABEL[active];
    return (
        <Button
            type="button"
            variant="outline"
            size={compact ? 'sm' : 'default'}
            title={label}
            aria-label={label}
            onClick={() => switchTo(active === 'v1' ? 'v2' : 'v1')}
            className={cn('px-2.5 font-mono text-xs font-semibold', className)}
        >
            {active.toUpperCase()}
        </Button>
    );
};

FlowToggle.displayName = 'FlowToggle';

export default FlowToggle;
